import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Truck, Package, CheckCircle, Clock, Printer, AlertTriangle, ExternalLink, Info, Route, Star, Loader2, X, Save, GripVertical } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { driverRouteService } from '@services/driverRouteService';
import { DriverRoute, DriverRouteStop } from '@/types/driverRoute';
import { stopRefLabel, stopTypeBadgeVariant, stopTypeLabel } from './driverRouteUtils';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import type { DriverRouteLive } from '@/types/vehicleTracker';
import { TrackLayer, VehicleMarker } from '@pages/fleetTracking/fleetMapLayers';
import { usePolling } from '@pages/fleetTracking/usePolling';
import { formatAgo } from '@pages/fleetTracking/trackerUtils';
import { FLEET_POLL_MS, formatClock, formatKm, trackDistanceKm } from '@pages/fleetTracking/fleetMapUtils';

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  // Strip Z/offset: backend returns Brasília local time, no UTC conversion should occur
  try { return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleString('pt-BR'); } catch { return dateStr; }
};

const formatTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return dateStr; }
};

const formatAddress = (stop: {
  address_street?: string; address_num?: string; address_compl?: string;
  address_district?: string; address_city?: string; address_state?: string;
}) => {
  const parts = [stop.address_street, stop.address_num, stop.address_compl, stop.address_district, stop.address_city, stop.address_state].filter(Boolean);
  return parts.join(', ') || '—';
};

const GeocodingInfo: React.FC<{ stop: DriverRouteStop }> = ({ stop }) => {
  if (stop.geocoding_status === 0 && stop.latitude != null && stop.longitude != null) {
    return (
      <div className="flex items-center gap-2 mt-1">
        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
          <MapPin size={10} /> Geolocalizado
        </span>
        <a
          href={`https://www.google.com/maps?q=${stop.latitude},${stop.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
        >
          <ExternalLink size={10} /> Ver no mapa
        </a>
      </div>
    );
  }

  if (stop.geocoding_status === 1) {
    return (
      <div className="flex items-center gap-1.5 mt-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
        <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
        <span>{stop.geocoding_message || 'Endereço não geocodificado'}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
      <Info size={12} /> Geolocalização não disponível
    </div>
  );
};

// Ponto fixo de partida e retorno da rota (depósito/base)
// Configurável via ROUTE_DEPOT_LATITUDE / ROUTE_DEPOT_LONGITUDE no backend
const DEPOT = {
  lat: -23.6283434,
  lng: -46.7376804,
  label: 'Base — Rua Aureliano Guimarães, 172, Vila Andrade, São Paulo - SP',
};

const createDepotIcon = () =>
  L.divIcon({
    className: '',
    html: `<div style="
      background: #16A34A;
      color: white;
      border-radius: 6px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.35);
    ">&#127968;</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

const createNumberedIcon = (number: number | string, isPriority: boolean, isDelivered: boolean = false) => {
  const color = isDelivered ? '#16A34A' : isPriority ? '#EF4444' : '#3B82F6';
  return L.divIcon({
    className: '',
    html: `<div style="
      background: ${color};
      color: white;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

const FitBounds: React.FC<{ stops: DriverRouteStop[] }> = ({ stops }) => {
  const map = useMap();
  useEffect(() => {
    // Inclui o depósito nos bounds para que o mapa mostre o ponto de partida
    const coords: [number, number][] = [
      [DEPOT.lat, DEPOT.lng],
      ...stops
        .filter((s) => s.latitude != null && s.longitude != null)
        .map((s) => [s.latitude!, s.longitude!] as [number, number]),
    ];
    if (coords.length > 1) {
      map.fitBounds(L.latLngBounds(coords), { padding: [40, 40] });
    }
  }, [stops, map]);
  return null;
};

export const DriverRouteDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [route, setRoute] = useState<DriverRoute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [showOptimizeConfirm, setShowOptimizeConfirm] = useState(false);

  // Manual coordinate editing
  const [editingCoordStop, setEditingCoordStop] = useState<DriverRouteStop | null>(null);
  const [coordLat, setCoordLat] = useState('');
  const [coordLng, setCoordLng] = useState('');
  const [coordSaving, setCoordSaving] = useState(false);
  const [coordError, setCoordError] = useState('');

  // Drag-and-drop reorder
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);

  // All derived values computed unconditionally before any early returns
  const stops = route?.stops ?? [];
  const pendingCount = stops.filter((s) => !s.is_delivered && !s.suspended_at).length;
  const totalCount = stops.length;
  const allDelivered = pendingCount === 0 && totalCount > 0;
  const isOptimized = totalCount > 0 && stops.every((s) => s.stop_order != null);
  const hasGeocodedStops = stops.some((s) => s.latitude != null && s.longitude != null);

  const sortedStops = useMemo(() => {
    return [...stops].sort((a, b) => {
      const orderA = a.stop_order ?? 999999;
      const orderB = b.stop_order ?? 999999;
      return orderA - orderB;
    });
  }, [stops]);

  useEffect(() => {
    if (id) loadRoute(id);
  }, [id]);

  // Veículo ao vivo e trajeto feito durante a rota (GPS). Atualiza a cada 10 s
  // enquanto a rota está em andamento ou o veículo volta à base; rota não
  // iniciada ou já fechada é lida uma vez. Falha aqui não atrapalha a tela da rota.
  const [live, setLive] = useState<DriverRouteLive | null>(null);
  const loadLive = useCallback(async () => {
    if (!id) return;
    try {
      setLive(await vehicleTrackerService.driverRouteLive(parseInt(id)));
    } catch {
      setLive(null);
    }
  }, [id]);
  usePolling(loadLive, FLEET_POLL_MS, !!id && (live == null || live.in_progress || live.returning_to_depot));
  const liveKm = useMemo(() => (live ? trackDistanceKm(live.points) : 0), [live]);

  const handleDragEnd = async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || !route) return;
    const reordered = [...sortedStops];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const stopIds = reordered.map(s => s.driver_route_stop_id);
    setReordering(true);
    try {
      const updated = await driverRouteService.reorderStops(route.driver_route_id, stopIds);
      setRoute(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao reordenar paradas');
    } finally {
      setReordering(false);
      setDragIndex(null);
      setDragOverIndex(null);
    }
  };

  const loadRoute = async (routeId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await driverRouteService.getById(parseInt(routeId));
      setRoute(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar rota');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="text-slate-500">Carregando...</div></div>;
  }

  if (error || !route) {
    return (
      <div className="space-y-4">
        <Button variant="secondary" onClick={() => navigate('/driver-routes')} icon={<ArrowLeft size={20} />}>Voltar</Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error || 'Rota não encontrada'}</div>
      </div>
    );
  }

  const handleDownloadPdf = async () => {
    if (!route) return;
    setPdfLoading(true);
    try {
      await driverRouteService.downloadPdf(route.driver_route_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleOptimize = async () => {
    if (!route) return;
    setShowOptimizeConfirm(false);
    setOptimizing(true);
    setOptimizeError(null);
    try {
      const optimizedRoute = await driverRouteService.optimizeRoute(route.driver_route_id);
      setRoute(optimizedRoute);
    } catch (err) {
      setOptimizeError(err instanceof Error ? err.message : 'Erro ao otimizar rota');
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/driver-routes')} icon={<ArrowLeft size={20} />} />
        <div className="flex-1">
          <h1 className="text-h1">Rota #{route.driver_route_id}</h1>
          <p className="text-slate-600">Detalhes da rota de entrega</p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGuard permission="driver_routes.read">
            <Button
              variant="secondary"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              icon={<Printer size={18} />}
            >
              {pdfLoading ? 'Gerando...' : 'PDF'}
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="driver_routes.write">
            {isOptimized && (
              <Badge variant="success">✓ Rota Otimizada</Badge>
            )}
            {totalCount >= 2 && stops.every((s) => s.latitude != null && s.longitude != null) ? (
              <Button
                onClick={() => setShowOptimizeConfirm(true)}
                disabled={optimizing}
                icon={optimizing ? <Loader2 size={18} className="animate-spin" /> : <Route size={18} />}
              >
                {optimizing ? 'Otimizando...' : isOptimized ? 'Re-otimizar Rota' : 'Otimizar Rota'}
              </Button>
            ) : stops.some((s) => s.geocoding_status === 1) ? (
              <Badge variant="warning">Aguardando geocodificação</Badge>
            ) : null}
          </PermissionGuard>
          <Badge variant={allDelivered ? 'success' : 'warning'}>
            {allDelivered ? 'Concluída' : `${pendingCount} pendente(s)`}
          </Badge>
          {route.driver_not_follow_route && (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"
              title="O motorista não seguiu a sequência das paradas planejadas"
            >
              <AlertTriangle size={12} /> Fora da sequência da rota
            </span>
          )}
        </div>
      </div>

      {/* Route Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><Truck size={20} className="text-emerald-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Motorista</p>
                <p className="font-medium">{route.driver?.full_name || `#${route.driver_id}`}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Package size={20} className="text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Veículo</p>
                <p className="font-medium">{route.vehicle?.description || `#${route.vehicle_id}`}</p>
                {route.vehicle?.license_plate && <p className="text-xs text-slate-400">{route.vehicle.license_plate}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><MapPin size={20} className="text-amber-600" /></div>
              <div>
                <p className="text-sm text-slate-500">KM do Veículo</p>
                <p className="font-medium">{route.vehicle_km ? `${route.vehicle_km.toLocaleString('pt-BR')} km` : '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Geocoding Warning */}
      {stops.some((s) => s.geocoding_status === 1) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Aviso de Geolocalização</p>
              <p className="text-sm mt-1">
                {stops.filter((s) => s.geocoding_status === 1).length} parada(s) não puderam ser geocodificadas.
                Corrija os endereços ou informe as coordenadas manualmente.
              </p>
              <div className="mt-3 space-y-2">
                {stops.filter((s) => s.geocoding_status === 1).map((stop) => (
                  <div key={stop.driver_route_stop_id} className="flex items-center gap-2 bg-white border border-amber-100 rounded-lg px-3 py-2">
                    <MapPin size={14} className="text-amber-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-700">
                        {stopRefLabel(stop)}
                        {stop.customer_name ? ` — ${stop.customer_name}` : ''}
                      </span>
                      <p className="text-xs text-slate-500 truncate">{formatAddress(stop)}</p>
                      {stop.geocoding_message && (
                        <p className="text-xs text-amber-600 italic">{stop.geocoding_message}</p>
                      )}
                    </div>
                    <PermissionGuard permission="driver_routes.write">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCoordStop(stop);
                          setCoordLat(stop.latitude?.toString() ?? '');
                          setCoordLng(stop.longitude?.toString() ?? '');
                          setCoordError('');
                        }}
                        className="px-2 py-1 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 rounded transition-colors whitespace-nowrap"
                      >
                        Informar coordenadas
                      </button>
                    </PermissionGuard>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Optimize Error */}
      {optimizeError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Erro na Otimização</p>
            <p className="text-sm mt-1">{optimizeError}</p>
          </div>
          <button onClick={() => setOptimizeError(null)} className="text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Route Map */}
      {(hasGeocodedStops || (live != null && (live.points.length > 0 || live.vehicle != null))) && (
        <Card>
          <CardHeader title={<span className="flex items-center gap-2"><MapPin size={20} /> Mapa da Rota {isOptimized && <Badge variant="success">Otimizada</Badge>}</span>} />
          {live && (
            <div className="px-4 pb-3 -mt-1 text-sm text-slate-600 flex flex-wrap items-center gap-2">
              <Truck size={14} />
              {!live.has_tracker && <span>O veículo desta rota não tem rastreador ativo.</span>}
              {live.has_tracker && !live.started_at && <span>O veículo aparece no mapa quando a entrega for iniciada.</span>}
              {live.has_tracker && live.in_progress && (
                <span>
                  <Badge variant="success">Ao vivo</Badge>{' '}
                  {live.vehicle
                    ? `comunicou ${formatAgo(live.vehicle.last_seen_seconds_ago)}${live.vehicle.battery_percent != null ? ` · bateria ${live.vehicle.battery_percent}%` : ''}`
                    : 'sem posição do veículo ainda'}
                  {live.points.length > 0 && ` · ${formatKm(liveKm)} desde ${formatClock(live.started_at)}`}
                </span>
              )}
              {live.has_tracker && live.returning_to_depot && (
                <span>
                  <Badge variant="info">Voltando à base</Badge>{' '}
                  entregas concluídas às {formatClock(live.finished_at)}
                  {live.vehicle && ` · comunicou ${formatAgo(live.vehicle.last_seen_seconds_ago)}`}
                  {live.points.length > 0 && ` · ${formatKm(liveKm)} desde ${formatClock(live.started_at)}`}
                </span>
              )}
              {live.has_tracker && live.started_at && !live.in_progress && !live.returning_to_depot && (
                <span>
                  Rota concluída: trajeto de {formatClock(live.started_at)} a{' '}
                  {live.returned_at ? `${formatClock(live.returned_at)} (volta à base)` : formatClock(live.finished_at)}
                  {live.points.length > 0 ? ` · ${formatKm(liveKm)} · ${live.stops.length} paradas detectadas` : ' · sem posições registradas'}
                </span>
              )}
            </div>
          )}
          <CardContent className="p-0">
            <div style={{ height: 400 }}>
              <MapContainer
                center={[-23.55, -46.63]}
                zoom={12}
                style={{ height: '100%', width: '100%', borderRadius: '0 0 0.75rem 0.75rem' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds stops={sortedStops} />

                {/* Marcador do depósito (ponto de partida e retorno) */}
                <Marker position={[DEPOT.lat, DEPOT.lng]} icon={createDepotIcon()}>
                  <Popup>
                    <div className="text-sm">
                      <strong>&#127968; Base / Depósito</strong>
                      <br />
                      <span className="text-slate-500">{DEPOT.label}</span>
                      <br />
                      <span className="text-emerald-600 text-xs">Ponto de partida e retorno da rota</span>
                    </div>
                  </Popup>
                </Marker>

                {/* Marcadores das paradas */}
                {(() => {
                  // Group stops at same coordinates to show multiple numbers
                  const geocoded = sortedStops.filter((s) => s.latitude != null && s.longitude != null);
                  const coordGroups = new Map<string, { stops: typeof geocoded; indices: number[] }>();
                  geocoded.forEach((stop, idx) => {
                    const key = `${stop.latitude!.toFixed(5)},${stop.longitude!.toFixed(5)}`;
                    if (!coordGroups.has(key)) coordGroups.set(key, { stops: [], indices: [] });
                    const g = coordGroups.get(key)!;
                    g.stops.push(stop);
                    g.indices.push(idx);
                  });
                  return Array.from(coordGroups.entries()).map(([, group]) => {
                    const firstStop = group.stops[0];
                    const numbers = group.stops.map(s => s.stop_order ?? (sortedStops.indexOf(s) + 1));
                    const label = numbers.join(',');
                    const hasPriority = group.stops.some(s => s.priority === true);
                    const allDelivered = group.stops.every(s => s.is_delivered);
                    return (
                      <Marker
                        key={firstStop.driver_route_stop_id}
                        position={[firstStop.latitude!, firstStop.longitude!]}
                        icon={createNumberedIcon(label, hasPriority, allDelivered)}
                      >
                        <Popup>
                          <div className="text-sm space-y-2">
                            {group.stops.map((stop) => (
                              <div key={stop.driver_route_stop_id}>
                                <strong>{stop.stop_order ?? (sortedStops.indexOf(stop) + 1)}. {stop.customer_name ?? 'Parada'}</strong>
                                {stop.priority && <span className="text-red-500 font-bold"> ★ PRIORIDADE</span>}
                                {stop.request_delivery_time && (
                                  <span className="text-blue-600 font-medium"> — Horário: {formatTime(stop.request_delivery_time)}</span>
                                )}
                                <br />
                                <span className="text-slate-600">{stop.reference_description ?? ''}</span>
                                <br />
                                <span className="text-slate-500">{formatAddress(stop)}</span>
                                <br />
                                <span>{stop.is_delivered ? `✅ Entregue${stop.delivered_at ? ` — ${formatDateTime(stop.delivered_at)}` : ''}` : stop.suspended_at ? `⛔ Suspensa — ${stop.suspend_reason ?? ''}` : '⏳ Pendente'}</span>
                              </div>
                            ))}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  });
                })()}

                {/* Polilinha: depósito → paradas em ordem → depósito */}
                {sortedStops.filter((s) => s.latitude != null && s.longitude != null).length >= 1 && (
                  <Polyline
                    positions={[
                      [DEPOT.lat, DEPOT.lng],
                      ...sortedStops
                        .filter((s) => s.latitude != null && s.longitude != null)
                        .map((s) => [s.latitude!, s.longitude!] as [number, number]),
                      [DEPOT.lat, DEPOT.lng],
                    ]}
                    color="#3B82F6"
                    weight={3}
                    opacity={0.8}
                  />
                )}

                {/* Trajeto real do veículo durante a rota (GPS) e posição atual */}
                {live && <TrackLayer points={live.points} stops={live.stops} color="#16A34A" />}
                {live?.vehicle && <VehicleMarker vehicle={live.vehicle} selected showRouteLink={false} />}
              </MapContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stops */}
      <Card>
        <CardHeader title={<span className="flex items-center gap-2"><MapPin size={20} /> Paradas ({totalCount}) {isOptimized && <span className="text-xs font-normal text-emerald-600">— ordem otimizada</span>} {reordering && <Loader2 size={14} className="animate-spin text-slate-400" />}</span>} />
        <CardContent>
          {sortedStops.length > 0 ? (
            <div className="space-y-3">
              {sortedStops.map((stop, idx) => (
                <div
                  key={stop.driver_route_stop_id}
                  draggable={!stop.is_delivered && !reordering}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(stop.driver_route_stop_id));
                    setDragIndex(idx);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverIndex(idx);
                  }}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex !== null) {
                      handleDragEnd(dragIndex, idx);
                    }
                  }}
                  onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                  className={`flex items-start gap-3 p-3 border rounded-lg transition-all ${
                    dragOverIndex === idx ? 'border-emerald-400 bg-emerald-50/50 shadow-md' : ''
                  } ${stop.is_delivered ? 'border-emerald-200 bg-emerald-50' : stop.priority ? 'border-red-200 bg-red-50/30' : 'border-slate-200'} ${
                    dragIndex === idx ? 'opacity-50' : ''
                  }`}
                >
                  {!stop.is_delivered && (
                    <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 mt-1">
                      <GripVertical size={16} />
                    </div>
                  )}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${stop.is_delivered ? 'bg-emerald-200 text-emerald-700' : stop.priority ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                    {stop.stop_order ?? idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={stopTypeBadgeVariant(stop.route_stop_type)}>
                        {stopTypeLabel(stop.route_stop_type)}
                      </Badge>
                      {stop.route_stop_type === 'O' && stop.is_emergency && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-white bg-red-600 px-1.5 py-0.5 rounded-full">
                          <AlertTriangle size={10} /> Emergência
                        </span>
                      )}
                      {stop.priority && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                          <Star size={10} fill="currentColor" /> Prioridade
                        </span>
                      )}
                      {stop.has_replacement_items && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">
                          ↩ Troca
                        </span>
                      )}
                      {stop.request_delivery_time && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                          <Clock size={10} /> {formatTime(stop.request_delivery_time)}
                        </span>
                      )}
                      <span className="text-sm font-medium">{stopRefLabel(stop)}</span>
                      {stop.is_delivered ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle size={12} /> Entregue {formatDateTime(stop.delivered_at ?? undefined)}
                        </span>
                      ) : stop.suspended_at ? (
                        <span className="flex items-center gap-1 text-xs text-slate-600" title={stop.suspend_reason ?? undefined}>
                          ⛔ Suspensa {formatDateTime(stop.suspended_at)}{stop.suspend_reason ? ` — ${stop.suspend_reason}` : ''}
                        </span>
                      ) : stop.live_tracking?.status === 'A' ? (
                        <span className="flex items-center gap-1 text-xs text-sky-700">
                          <Clock size={12} /> Em rota (cliente acompanhando)
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-amber-600">
                          <Clock size={12} /> Pendente
                        </span>
                      )}
                    </div>
                    {stop.reference_description && stop.reference_description !== stopRefLabel(stop) && (
                      <p className="text-sm text-slate-700 mt-1">{stop.reference_description}</p>
                    )}
                    {stop.route_stop_type === 'R' && stop.pickup_description && (
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                        <span className="font-medium">Descrição:</span> {stop.pickup_description}
                      </p>
                    )}
                    {stop.route_stop_type === 'O' && stop.goods_delivery_description && (
                      <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1 mt-1">
                        <span className="font-medium">Descrição:</span> {stop.goods_delivery_description}
                      </p>
                    )}
                    {stop.customer_name && (
                      <p className="text-sm text-slate-600 mt-0.5">
                        {stop.route_stop_type === 'O' || stop.route_stop_type === 'D' ? 'Destinatário' : 'Cliente'}: {stop.customer_name}
                      </p>
                    )}
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                      <MapPin size={12} /> {formatAddress(stop)}
                    </p>
                    <GeocodingInfo stop={stop} />
                    {stop.priority && stop.estimate_delay_in_minutes != null && !stop.is_delivered && (
                      <div className="flex items-center gap-1.5 mt-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded inline-flex">
                        <Clock size={12} />
                        Previsão de chegada: {(() => {
                          const now = new Date();
                          const eta = new Date(now.getTime() + stop.estimate_delay_in_minutes * 60000);
                          return eta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                        })()} ({stop.estimate_delay_in_minutes} min)
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">Nenhuma parada registrada</div>
          )}
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-slate-500">Criado em:</span><br />{formatDateTime(route.created_at)}</div>
            <div><span className="text-slate-500">Atualizado em:</span><br />{formatDateTime(route.updated_at)}</div>
            <div><span className="text-slate-500">Criado por:</span><br />{route.created_by || '—'}</div>
            <div><span className="text-slate-500">Atualizado por:</span><br />{route.last_updated_by || '—'}</div>
          </div>
        </CardContent>
      </Card>

      {/* Coordinate Editing Modal */}
      {editingCoordStop && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 2000 }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Informar Coordenadas</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {stopRefLabel(editingCoordStop)}
                  {editingCoordStop.customer_name ? ` — ${editingCoordStop.customer_name}` : ''}
                </p>
              </div>
              <button onClick={() => setEditingCoordStop(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
              <p className="flex items-center gap-1"><MapPin size={12} /> {formatAddress(editingCoordStop)}</p>
              {editingCoordStop.geocoding_message && (
                <p className="text-xs text-amber-600 mt-1 italic">{editingCoordStop.geocoding_message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Latitude</label>
                <input
                  type="text"
                  value={coordLat}
                  onChange={(e) => setCoordLat(e.target.value)}
                  placeholder="-23.550520"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Longitude</label>
                <input
                  type="text"
                  value={coordLng}
                  onChange={(e) => setCoordLng(e.target.value)}
                  placeholder="-46.633308"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Dica: use Google Maps para encontrar as coordenadas (clique com botão direito no local e copie as coordenadas).
            </p>
            {coordError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{coordError}</div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setEditingCoordStop(null)}>Cancelar</Button>
              <Button
                disabled={coordSaving}
                icon={<Save size={16} />}
                onClick={async () => {
                  const lat = parseFloat(coordLat);
                  const lng = parseFloat(coordLng);
                  if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    setCoordError('Latitude deve estar entre -90 e 90, Longitude entre -180 e 180.');
                    return;
                  }
                  setCoordSaving(true);
                  setCoordError('');
                  try {
                    await driverRouteService.updateStopCoordinates(
                      route!.driver_route_id,
                      editingCoordStop.driver_route_stop_id,
                      lat,
                      lng
                    );
                    // Reload route to reflect changes
                    const updated = await driverRouteService.getById(route!.driver_route_id);
                    setRoute(updated);
                    setEditingCoordStop(null);
                  } catch (err) {
                    setCoordError(err instanceof Error ? err.message : 'Erro ao salvar coordenadas');
                  } finally {
                    setCoordSaving(false);
                  }
                }}
              >
                {coordSaving ? 'Salvando...' : 'Salvar Coordenadas'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Optimize Confirmation Modal — rendered via portal to escape Leaflet z-index stacking */}
      {showOptimizeConfirm && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 2000 }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Route size={24} className="text-blue-600" /></div>
              <div>
                <h3 className="text-lg font-semibold">Otimizar Rota?</h3>
                <p className="text-sm text-slate-600">Esta ação utiliza a Google Route Optimization API para reordenar as paradas de forma otimizada.</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600 space-y-1">
              <p>• <strong>{stops.length}</strong> parada(s) serão reordenadas</p>
              {stops.some((s) => s.priority) && (
                <p>• <strong>{stops.filter((s) => s.priority).length}</strong> parada(s) marcada(s) como prioridade</p>
              )}
              <p>• A ordem atual será substituída pela ordem otimizada</p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowOptimizeConfirm(false)}>Cancelar</Button>
              <Button onClick={handleOptimize} icon={<Route size={18} />}>Confirmar Otimização</Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
